import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import useStyles from './styles'

const CoHostManager = ({ open, onClose, presentationId }) => {
  const classes = useStyles();
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(2); 

  const userData = JSON.parse(sessionStorage.getItem('userData'));

  // Fetch current permissions when dialog opens
  useEffect(() => {
    if (open && presentationId) {
      fetchPermissions();
    }
  }, [open, presentationId]);

  const fetchPermissions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/v2/livemeeting/permissions/${presentationId}`
      );

      if (response.data.success) {
        setPermissions(response.data.permissions || []);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load current permissions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/v2/livemeeting/users/search?email=${encodeURIComponent(
          searchEmail
        )}`
      );

      if (response.data.success && response.data.users.length > 0) {
        setSearchResults(response.data.users);
      } else {
        setSearchResults([]);
        toast.error('No users found with this email');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddCoHost = async (user) => {
    // Validation: Check if user already exists in permissions
    const existingUser = permissions.find((perm) => perm.userid === user.id);
    
    if (existingUser) {
      if (existingUser.role === selectedRole) {
        toast.error(`This user is already added as ${getRoleName(selectedRole)}`);
        return;
      } else {
        toast.error(
          `This user is already added as ${getRoleName(existingUser.role)}. Cannot add as ${getRoleName(selectedRole)}`
        );
        return;
      }
    }

    // Validation: Check if role limit is reached
    const roleCount = permissions.filter((perm) => perm.role === selectedRole).length;
    
    if (selectedRole === 1 && roleCount >= 1) {
      toast.error('Only one Host is allowed per live studio');
      return;
    }
    
    if (selectedRole === 2 && roleCount >= 1) {
      toast.error('Only one Co-Host is allowed per live studio');
      return;
    }

    try {
      const payload = {
        domainid: userData.domainId,
        presentationid: presentationId,
        userid: user.id,
        role: selectedRole, 
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/v2/livemeeting/permissions/save`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(`${getRoleName(selectedRole)} added successfully`);
        setSearchResults([]);
        setSearchEmail('');
        fetchPermissions(); 
      }
    } catch (error) {
      console.error('Error adding co-host:', error);
      toast.error(`Failed to add ${getRoleName(selectedRole)}`);
    }
  };

  const handleRemovePermission = async (permissionId) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/v2/livemeeting/permissions/${permissionId}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Permission removed successfully');
        fetchPermissions(); 
      }
    } catch (error) {
      console.error('Error removing permission:', error);
      toast.error('Failed to remove permission');
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 1:
        return 'Host';
      case 2:
        return 'Co-Host';
      default:
        return 'Unknown';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        Live Studio - Manage Co-Hosts
      </DialogTitle>
      <DialogContent style={{ marginTop: '20px' }}>
        {/* Info Message */}
        <Box
          style={{
            backgroundColor: '#2d3748',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px',
            borderLeft: '4px solid #2196F3',
          }}
        >
          <Typography variant="body2" style={{ color: '#aaa', fontSize: '13px' }}>
            ℹ️ <strong>Note:</strong> Only one Host and one Co-Host are allowed per live studio. 
            A user cannot be added as both Host and Co-Host.
          </Typography>
        </Box>

        {/* Search Section */}
        <Typography variant="body2" style={{ color: '#aaa', marginBottom: '12px' }}>
          Search and add a co-host for this video's live studio.
        </Typography>

        <div className={classes.searchContainer}>
          <TextField
            className={classes.searchInput}
            label="Email Address"
            variant="outlined"
            size="small"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="user@example.com"
          />
          <Button
            className={classes.searchButton}
            variant="contained"
            startIcon={isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={isSearching}
          >
            Search
          </Button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Box className={classes.searchResults}>
            <Typography variant="subtitle2" style={{ marginBottom: '12px', fontWeight: 'bold' }}>
              Search Results
            </Typography>
            {searchResults.map((user) => {
              // Check if user already exists
              const existingUser = permissions.find((perm) => perm.userid === user.id);
              const isUserAlreadyAdded = !!existingUser;
              
              // Check if selected role slot is already filled
              const roleCount = permissions.filter((perm) => perm.role === selectedRole).length;
              const isRoleSlotFilled = roleCount >= 1;
              
              // Determine if add button should be disabled
              const isAddDisabled = isUserAlreadyAdded || isRoleSlotFilled;
              
              return (
                <Box
                  key={user.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: '#1a1a1a',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    opacity: isAddDisabled ? 0.6 : 1,
                  }}
                >
                  <div>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      {user.firstname} {user.lastname}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#aaa' }}>
                      {user.Email}
                    </Typography>
                    {isUserAlreadyAdded && (
                      <Typography variant="caption" style={{ color: '#ff6b6b' }}>
                        Already added as {getRoleName(existingUser.role)}
                      </Typography>
                    )}
                    {!isUserAlreadyAdded && isRoleSlotFilled && (
                      <Typography variant="caption" style={{ color: '#ffa726' }}>
                        {getRoleName(selectedRole)} slot already filled
                      </Typography>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <FormControl variant="outlined" size="small">
                      <Select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className={classes.select}
                        disabled={isUserAlreadyAdded}
                      >
                        <MenuItem value={2} className={classes.menuItem}>
                          Co-Host
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      className={classes.addButton}
                      variant="contained"
                      size="small"
                      onClick={() => handleAddCoHost(user)}
                      disabled={isAddDisabled}
                      style={{
                        opacity: isAddDisabled ? 0.5 : 1,
                        cursor: isAddDisabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Current Permissions Table */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', marginBottom: '12px' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
            Current Live Studio Permissions
          </Typography>
          <Box style={{ display: 'flex', gap: '16px' }}>
            <Typography variant="caption" style={{ color: '#aaa' }}>
              Host: {permissions.filter(p => p.role === 1).length}/1
            </Typography>
            <Typography variant="caption" style={{ color: '#aaa' }}>
              Co-Host: {permissions.filter(p => p.role === 2).length}/1
            </Typography>
          </Box>
        </Box>

        {isLoading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" style={{ color: '#aaa' }}>
                      No permissions found
                    </TableCell>
                  </TableRow>
                ) : (
                  permissions.map((permission) => (
                    <TableRow key={permission.id}>
                      <TableCell>
                        {permission.firstname} {permission.lastname}
                      </TableCell>
                      <TableCell>{permission.email}</TableCell>
                      <TableCell>
                        <span
                          className={`${classes.roleBadge} ${
                            permission.role === 1 ? classes.hostBadge : classes.coHostBadge
                          }`}
                        >
                          {getRoleName(permission.role)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        {permission.role !== 1 && ( 
                          <IconButton
                            className={classes.removeButton}
                            size="small"
                            onClick={() => handleRemovePermission(permission.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            style={{ color: 'white', borderColor: '#666' }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CoHostManager;